import React, { useEffect, useRef, useState } from 'react';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';

import MessageBox from '../components/MessageBox.jsx';
import fetchApi from '../lib/fetchApi.js';
import { validateValue } from '../lib/functions.js';

const USER_GROUPS = [
  { id: 1, name: '超級管理員' },
  { id: 2, name: '管理員' },
  { id: 3, name: '一般用戶' },
];

const DEFAULT_USER = {
  id: '',
  fullname: '',
  nickname: '',
  username: '',
  password: '',
  mobile: '',
  email: '',
  user_group_id: 3,
  user_group: USER_GROUPS[2],
};

const User = ({ setPageParams }) => {
  const toast = useRef(null);
  const [users, setUsers] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userDetail, setUserDetail] = useState(DEFAULT_USER);
  const [isUnsaved, setIsUnsaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [messageBoxData, setMessageBoxData] = useState({
    show: false, title: '', content: '', type: 'warning',
  });

  useEffect(() => {
    setPageParams({ alias: 'user', title: '用戶管理' });
    refresh();
  }, []);

  const refresh = async () => {
    const response = await fetchApi('/api/v1/user/getUsers');
    if (response?.data?.users) {
      setUsers(response.data.users.map((u) => ({ ...u, user_group_name: u.user_group?.name })));
    } else if (response?.error?.includes?.('permission_denied')) {
      toast.current?.show({ severity: 'error', summary: '用戶管理', detail: '你沒有權限查看用戶。' });
    }
  };

  const startCreate = () => {
    setUserDetail(DEFAULT_USER);
    setIsEditing(false);
    setIsOpenDetail(true);
    setIsUnsaved(false);
    setIsSaving(false);
  };

  const startEdit = async (userId) => {
    setIsLoadingDetail(true);
    setIsEditing(true);
    setIsOpenDetail(true);
    setIsUnsaved(false);
    setIsSaving(false);
    const response = await fetchApi('/api/v1/user/getUsers', { user_id: userId });
    if (response?.data?.users?.[0]) {
      const u = response.data.users[0];
      setUserDetail({
        ...u,
        password: '',
        user_group: USER_GROUPS.find((g) => g.id === u.user_group_id) || USER_GROUPS[2],
      });
    }
    setIsLoadingDetail(false);
  };

  const updateUserDetail = (field, value) => {
    setIsUnsaved(true);
    if (field === 'user_group') {
      setUserDetail((d) => ({ ...d, user_group_id: value.id, user_group: value }));
    } else {
      setUserDetail((d) => ({ ...d, [field]: value }));
    }
  };

  const save = async () => {
    // Defensive: trim/validate against possibly-null strings from the API
    // even though the backend normalises them. Keeps the modal from NPEing
    // if a stale state lingers after an HMR reload.
    const str = (v) => (v == null ? '' : String(v));
    const errors = [];
    if (!str(userDetail.fullname).trim()) errors.push('必須輸入姓名');
    if (!str(userDetail.username).trim()) errors.push('必須輸入登入名稱');
    if (!isEditing && !validateValue('password', userDetail.password)) errors.push('密碼最少為 6 位字元');
    const email = str(userDetail.email).trim();
    if (email && !validateValue('email', email)) errors.push('電郵格式不正確');
    if (errors.length > 0) {
      setMessageBoxData({
        show: true, title: '資料錯誤',
        content: <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>,
        type: 'warning',
      });
      return;
    }
    setIsSaving(true);
    const response = await fetchApi('/api/v1/user/saveUser', userDetail);
    if (response.state === 1) {
      toast.current?.show({
        severity: 'success', summary: '用戶管理',
        detail: isEditing ? '已成功儲存用戶資料。' : '已成功新增用戶。',
      });
      setIsUnsaved(false);
      refresh();
    } else if (response.error?.[0] === 'duplicated_username') {
      setMessageBoxData({
        show: true, title: '資料錯誤',
        content: <ul><li>登入名稱「{userDetail.username}」已被使用</li></ul>,
        type: 'danger',
      });
    }
    setIsSaving(false);
  };

  const onTableAreaClick = (e) => {
    // 點到「非資料列」的任何地方（表頭、空區、paginator、列與列之間的 padding）→ 清除選取
    // 詳見 DIRECTIONS.md §0 的 DataTable 詞條。
    if (!e.target.closest('tbody tr')) {
      setSelectedUser(null);
    }
  };

  const closeDetail = () => {
    if (isUnsaved) {
      confirmDialog({
        message: '你還未儲存變更，確定要關閉？',
        header: '未儲存變更',
        icon: 'pi pi-info-circle',
        defaultFocus: 'reject',
        acceptClassName: 'p-button-warning',
        accept: () => setIsOpenDetail(false),
      });
    } else {
      setIsOpenDetail(false);
    }
  };

  return (
    <>
      <Toolbar
        className="likkan-toolbar mb-3"
        pt={{
          start: { className: 'flex align-items-center gap-2' },
          end:   { className: 'flex align-items-center gap-2' },
        }}
        start={<div>用戶列表</div>}
        end={<Button label="新增" onClick={startCreate} icon="pi pi-plus" size="small" />}
      />

      <div onClick={onTableAreaClick}>
        <DataTable
          value={users || []}
          loading={users === null}
          selectionMode="single"
          onRowClick={(e) => startEdit(e.data.id)}
          emptyMessage="未有記錄"
          selection={selectedUser}
          onSelectionChange={(e) => {
            // PrimeReact 在 single-selection 模式下，點同一列會 emit value=null
            // 想取消選取。我們不要這個行為：點同一列保持選取；只能點別列才換。
            // 詳見 DIRECTIONS.md §0 的 DataTable 詞條。
            if (e.value !== null && e.value !== undefined) setSelectedUser(e.value);
          }}
          stripedRows
          dataKey="id"
          filterDisplay="row"
        >
          <Column
            field="fullname"
            header="全名"
            filter
            filterMatchMode="contains"
            showFilterMenu={false}
            filterPlaceholder="搜尋全名"
          />
          <Column
            field="nickname"
            header="暱稱"
            filter
            filterMatchMode="contains"
            showFilterMenu={false}
            filterPlaceholder="搜尋暱稱"
          />
          <Column
            field="user_group_id"
            header="用戶組"
            body={(r) => r.user_group?.name}
            filter
            filterMatchMode="custom"
            showFilterMenu={false}
            filterElement={(options) => (
              <MultiSelect
                value={options.value}
                options={USER_GROUPS}
                onChange={(e) => options.filterApplyCallback(e.value)}
                optionLabel="name"
                optionValue="id"
                placeholder="全部"
                display="chip"
                style={{ minWidth: '12rem' }}
              />
            )}
            filterFunction={(value, filter) => {
              if (!filter || !filter.length) return true;
              // Medoo 透過 PDO 把 BIGINT 回成字串；MultiSelect emit 的是 number。
              // 不轉型永遠 includes 不上。詳見 DIRECTIONS.md §6 bug log。
              const numericValue = Number(value);
              return filter.map(Number).includes(numericValue);
            }}
          />
        </DataTable>
      </div>

      <Dialog
        className="likkan-detail-modal"
        visible={isOpenDetail}
        onHide={() => setIsOpenDetail(false)}
        header={isEditing ? userDetail.fullname : '新增用戶'}
        footer={
          <>
            <Button label="關閉" onClick={closeDetail} outlined disabled={isSaving} />
            <Button label="儲存" onClick={save} disabled={isLoadingDetail || isSaving} />
          </>
        }
      >
        {isLoadingDetail ? (
          <div className="flex justify-content-center m-6">
            <ProgressSpinner strokeWidth="3" style={{ width: '32px', height: '32px' }} />
          </div>
        ) : (
          <>
            <div className="likkan-form-grid likkan-form-grid-2">
              <div className="likkan-field">
                <label>姓名</label>
                <InputText value={userDetail.fullname} onChange={(e) => updateUserDetail('fullname', e.target.value)} />
              </div>
              <div className="likkan-field">
                <label>暱稱</label>
                <InputText value={userDetail.nickname} onChange={(e) => updateUserDetail('nickname', e.target.value)} />
              </div>
              <div className="likkan-field">
                <label>電話</label>
                <InputText value={userDetail.mobile} onChange={(e) => updateUserDetail('mobile', e.target.value)} />
              </div>
              <div className="likkan-field">
                <label>電郵</label>
                <InputText value={userDetail.email} onChange={(e) => updateUserDetail('email', e.target.value)} />
              </div>
            </div>
            <Divider />
            <div className="likkan-form-grid likkan-form-grid-2">
              <div className="likkan-field">
                <label>登入名稱</label>
                <InputText value={userDetail.username} disabled={isEditing} onChange={(e) => updateUserDetail('username', e.target.value)} />
                <small>{isEditing ? '無法修改。' : '用戶將使用此名稱登入系統。'}</small>
              </div>
              <div className="likkan-field">
                <label>密碼</label>
                <InputText value={userDetail.password} onChange={(e) => updateUserDetail('password', e.target.value)} />
                <small>{isEditing ? '如不修改密碼，請留空。' : '用戶將使用此密碼登入系統。'}</small>
              </div>
            </div>
            <Divider />
            <div className="likkan-form-grid">
              <div className="likkan-field">
                <label>用戶組</label>
                <Dropdown
                  value={userDetail.user_group}
                  onChange={(e) => updateUserDetail('user_group', e.value)}
                  options={USER_GROUPS}
                  optionLabel="name"
                  placeholder="選取一項"
                  className="w-full"
                />
              </div>
            </div>
          </>
        )}
      </Dialog>

      <ConfirmDialog />
      <MessageBox
        visible={messageBoxData.show}
        title={messageBoxData.title}
        content={messageBoxData.content}
        type={messageBoxData.type}
        onHide={() => setMessageBoxData((m) => ({ ...m, show: false }))}
      />
      <Toast ref={toast} position="top-center" />
    </>
  );
};

export default User;
